import { DecimalPipe } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { Component, Inject, Injector, ViewChild } from "@angular/core";
import { ColumnBase, ColumnComponent, ColumnMenuSettings, ColumnReorderEvent, ColumnResizeArgs, ColumnVisibilityChangeEvent, DataStateChangeEvent, GridComponent, GridDataResult, RowClassArgs, SelectableSettings } from "@progress/kendo-angular-grid";
import { CompositeFilterDescriptor, State, process } from "@progress/kendo-data-query";
import { Observable, of } from "rxjs";
import { take } from "rxjs/operators";
import { TableSettingsColumn } from "../../../models/table-settings-column";
import { TableSettingsService } from "../../../services/TableSettingsService";
import { DestroyBase } from "../ng-destroy-base";
import { PolluxGridConfig } from "../pollux-grid-config/pollux-grid-config";
import { WorkspaceBase } from "../workspace-base/workspace-base";
import { GridPagerConfig } from "../grid-pager-config/grid-pager-config";
import { DateUtil } from "../date-util/date-util";
import { ArraysUtil } from "../arrays-util/arrays-util";
import { AppServiceHelperService } from "../../../services/app-service-helper.service";
import { EventBusService } from "../../../services/event-bus.service";
import { ObjectUtil } from "../object-util/object-util";

/**
 * Grid base class
 */
@Component({ template: '' })
export abstract class GridBaseComponent extends DestroyBase {
  /**
   * ViewChild reference to the GridComponent for accessing grid-related functionality.
   */
  @ViewChild(GridComponent) public kendoGrid: GridComponent;

  /**
   * Pager config
   */
  protected pagerConfig: GridPagerConfig = {
    pageSize: 50,
    sizes: [10, 25, 50, 100],
    numericButtonsCount: 0
  }

  /**
   * Pager info message (f.e. 1 - 50 OF 27.136 ITEMS)
   */
  protected get pagerInfoMessage(): string {
    const from: number = Math.min(this.state.skip + 1, this.gridDataResult.total);
    const to: number = Math.min(this.state.skip + this.state.take, this.gridDataResult.total);
    const itemsCount: number = this.gridDataResult.total;
    return `${this.decimalPipe.transform(from, '1.0-0')} - ${this.decimalPipe.transform(to, '1.0-0')} OF ${this.decimalPipe.transform(itemsCount, '1.0-0')} ITEMS`;
  }

  /**
   * Selected item
   */
  private _selectedItem: any;

  /**
   * Selected item
   */
  public get selectedItem(): any {
    return this._selectedItem;
  }

  /**
   * Grid columns
   */
  public columns: PolluxGridConfig[] = [];

  /**
   * Gets a list of columns
   */
  public abstract getColumns(): PolluxGridConfig[];

  /**
   * Clicked row item. Used for doubleclick detection
   */
  private clickedRowItem: any;

  /**
   * Save selected items
   */
  public selectedItems: any[] = [];

  /**
   * Grid SelectableSettings
   */
  public selectableSettings: SelectableSettings = {
    enabled: true,
    mode: 'single'
  };

  /**
   * Grid ColumnMenuSettings
   */
  public columnMenuSettings: ColumnMenuSettings = {
    filter: true
  };

  /**
   * The current grid state
   */
  public state: State = {
    skip: 0,
    take: this.pagerConfig.pageSize,
    group: [],
    filter: { filters: [], logic: "and" },
    sort: []
  };

  /**
   * Grid GridDataResult. Private.. please use getFlattenGridDataResultData()
   */
  public gridDataResult: GridDataResult = { data: [], total: 0 };

  /**
   * Current page items (different than kendoGrid.data or kendoGrid.view - depends on grouping)
   */
  public currentPageItems: any[];

  /**
   * Loading indicator
   */
  public loading = false;

  /**
   * The workspace reference
   */
  public abstract get workspaceReference(): WorkspaceBase;

  /**
   * Original data loaded for grid
   */
  private loadedData: any = null;

  /**
   * The Angular HTTP Client
   */
  protected http: HttpClient;

  /**
   * The event bus
   */
  protected eventBus: EventBusService;

  /**
   * App service helper for getting API URLs
   */
  protected appServiceHelper: AppServiceHelperService;

  /**
   * The table settings service
   */
  protected tableSettingsService: TableSettingsService;

  /**
   * The Angular decimal pipe
   */
  protected decimalPipe: DecimalPipe;

  /**
   * Constructor
   * @param injector The Angular injector
   * @param area The area
   * @param useServerSidePaging Indicates if this grid uses server side paging. Preferrably, this should be set to true. Only use other config if absolutely necessary
   */
  constructor(protected injector: Injector, @Inject(String) protected area: string, @Inject(Boolean) private useServerSidePaging: boolean) {
    super();
    //inject required services
    this.http = injector.get(HttpClient);
    this.eventBus = injector.get(EventBusService);
    this.tableSettingsService = injector.get(TableSettingsService);
    this.decimalPipe = injector.get(DecimalPipe);
    this.appServiceHelper = injector.get(AppServiceHelperService);
  }

  /**
   * Initializes the form
   * @returns A Promise that should be awaited
   */
  protected init(): Promise<void> {
    this.registerEvents();
    if (!this.workspaceReference) {
      console.error('No workspace reference! Something was configured incorrectly!');
      return;
    }

    this.workspaceReference.grid = this;

    return new Promise<void>((resolve) => {
      Promise.all([
        this.loadGridSettings(),
        this.loadData({
          take: this.pagerConfig.pageSize,
          skip: 0,
          filter: null,
          group: null,
          sort: null
        }, null)
      ]).then(() => {
        resolve();
      });
    });
  }

  /**
   * Loads the grid settings
   * @returns Promise to wait for
   */
  protected loadGridSettings(): Promise<void> {
    this.columns = this.getColumns();
    this.columns.forEach((column, i) => {
      column.index = i;
    });

    return new Promise((resolve, reject) => {
      this.tableSettingsService.load(this.area).then(
        (gridSettings: TableSettingsColumn[]) => {
          if (gridSettings) {
            this.tableSettingsService.processGrid(this.area, this.kendoGrid.columns.toArray() as ColumnComponent[]);
          }
          resolve();
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  /**
   * Checkbox state
   */
  public checkAllChecked = false;

  /**
   * Select or deselect all checkboxes based on checkAllChecked state
   */
  public selectOrDeselectAllCheckbox(): void {
    for (const item of this.getFlattenGridDataResultData()) {
      item.checkbox = this.checkAllChecked;
    }
  }

  /**
   * Gets the url for loading a paged list
   * If server paging is not supported return null and override getGridDataUrlWithQueryString
   * @returns The url for loading a paged list
   */
  protected abstract getPagedListEndpoint(): string;

  /**
   * Gets the grid data URL with query string. Preferrably, use getPagedListEndpoint and only use this method if absolutely necessary.
   * @returns
   */
  protected getGridDataUrlWithQueryString(): string {
    return "";
  }

  /**
   * The data state was changed
   * @param gridDataState The new data state
   * @returns 
   */
  public dataStateChanged(gridDataState: DataStateChangeEvent): Promise<any> {
    if (this.shouldTriggerLoad(gridDataState) && this.useServerSidePaging) {
      return this.loadData(gridDataState, null);
    } else {
      this.setState(gridDataState);
      this.processGrid(this.loadedData, this.gridDataResult.total);
    }
  }

  /**
   * Builds the paged list endpoint URL
   * @param gridDataState The grid's state
   * @returns The URL to be called to get paged data
   */
  private buildPagedListEndpoint(gridDataState: State): string {
    const pagedListUrl: string = this.getPagedListEndpoint();
    if (pagedListUrl) {
      const filterQueryString = this.getFilterQueryString(gridDataState.filter);
      const page = (gridDataState.skip / gridDataState.take) + 1;
      const queryStringFirstSeparator = pagedListUrl.indexOf('?') === -1 ? '?' : '&';//pagedListUrl could already contain ?
      const url = `${pagedListUrl}${queryStringFirstSeparator}skip=${gridDataState.skip}&take=${gridDataState.take}&page=${page}&pageSize=${gridDataState.take}${filterQueryString}`;
      return url;
    }

    return null;
  }

  /**
   * Loads data
   * @param gridDataState
   * @param selectedItemOid The selected item OID. If not null, this item will be selected after loading if it appears on the loaded page
   * @returns A Promise to wait for
   */
  public loadData(gridDataState: State, selectedItemOid: number = null): Promise<any> {
    return new Promise((resolve, reject) => {
      this.loading = true;

      if (!gridDataState) {
        gridDataState = {};
      }

      if (!gridDataState.take) {
        gridDataState.take = this.pagerConfig.pageSize;
      }

      if (!gridDataState.group) {
        gridDataState.group = this.state.group;
      }

      const url = this.useServerSidePaging ? this.buildPagedListEndpoint(gridDataState) : this.getGridDataUrlWithQueryString();
      //if no url provided emit empty result
      const data$: Observable<any> = url ? this.http.get(url) : of([]);
      data$.pipe(take(1)).subscribe({
        next: (data: any) => {
          selectedItemOid = this.handleDataLoaded(gridDataState, data, selectedItemOid, resolve);
        },
        error: (error: any) => {
          this.loading = false;
          reject(error);
        }
      });
    });
  }

  /**
   * Handles loading of data
   * @param gridDataState Grid data state
   * @param data Data
   * @param selectedItemOid The selected item's OID
   * @param resolve resolve callback
   * @returns The selected item's oid
   */
  private handleDataLoaded(gridDataState: State, data: any, selectedItemOid: number, resolve: (value: any) => void): number {
    this.loading = false;
    this.setState(gridDataState);

    if (data) {
      this.gridDataResult.data = this.useServerSidePaging ? data.details : data;
      this.gridDataResult.total = this.useServerSidePaging ? data.total : data.length;
      this.parseDatesOnLoad(this.gridDataResult.data);
    }
    else {
      this.gridDataResult.data = [];
      this.gridDataResult.total = 0;
    }

    //remember original loaded data for grids that doesn't use server side paging/filtering
    this.loadedData = this.gridDataResult.data;
    this.processGrid(this.loadedData, this.gridDataResult.total);

    let removeRowSelection = true;
    selectedItemOid = +selectedItemOid || this.workspaceReference.oid;
    if (selectedItemOid > 0) {
      //Keep selection if item is in view (on current page)
      const oidColumnName = this.workspaceReference.oidPropertyName;
      const items = this.currentPageItems.filter(item => item[oidColumnName] === +selectedItemOid);
      if (items.length > 0) {
        this.selectedItems = [selectedItemOid];
        const item = items[0];
        this._selectedItem = item;
        this.handleRowSelection(null, item);
        removeRowSelection = false;
      }
    }

    if (removeRowSelection) {
      this.selectedItems = [];
      this._selectedItem = null;
      this.handleRowSelection(null, null);
    }

    this.onDataLoaded(this.gridDataResult);
    resolve(data);
    return selectedItemOid;
  }

  /**
   * Data loading callback
   * @param data The loaded data
   */
  protected abstract onDataLoaded(data: GridDataResult): void;

  /**
   * Handle a row selection / deselection event
   * @param event The event. Can be null!
   * @param item The item. If an item was deselected, it's null
   */
  protected abstract handleRowSelection(event: any, item: any): void;

  /**
   * Handles if a cell was clicked
   * @param _event The event. Can be null!
   */
  protected handleCellClick(_event: any): void {
    //Nothing. Can be overridden
  }

  /**
   * Handle a doubleclick event
   * @param event The event. Can be null!
   * @param item The item
   */
  protected abstract handleDoubleClick(event: any, item: any): void;

  /**
   * Applies a filter and loads the filtered data
   * @param filter The filter
   */
  public applyFilter(filter: any, selectedItemOid: number = null): Promise<any> {
    return this.loadData({
      take: this.pagerConfig.pageSize,
      skip: 0,
      filter: filter,
      group: null,
      sort: null
    }, selectedItemOid);
  }

  /**
   * Reset the filters and show all items
   */
  public showAll(selectedItemOid: number = null): void {
    this.loadData({
      take: this.pagerConfig.pageSize,
      skip: 0,
      filter: null,
      group: null,
      sort: null
    }, selectedItemOid);
  }

  /**
   * Important: All date values are treated as UTC dates in the backend.
   * Therefore, please parse all date values into a Date by using this.getDate within this method.
   * This step occurs directly before the createFormGroup step
   * @param data The loaded and post-processed data
   */
  protected parseDatesOnLoad(data: any[]): void {
    if (data?.length) {
      for (const field of this.columns) {
        if (field.type === 'date') {
          for (const row of data) {
            row[field.field] = DateUtil.getDate(row[field.field]);
          }
        }
      }
    }
  }

  /**
   * On row selection
   * @param event
   */
  public onRowSelected(event: any): void {
    if (event.selectedRows.length > 0) {
      this._selectedItem = event.selectedRows[0].dataItem;
    }
    else {
      this._selectedItem = null;
    }
    this.handleRowSelection(event, this._selectedItem);
  }

  /**
   * A cell was clicked. Remember the current row item for doubleclick detection
   * @param event
   */
  public onCellClick(event: any): void {
    this.clickedRowItem = event.dataItem;
    this.handleCellClick(event);
  }

  /**
   * A ddoubleclick event was detected
   * @param event
   */
  public onDoubleClicked(_event: any): void {
    if (this.clickedRowItem) {
      this.handleDoubleClick(_event, this.clickedRowItem);
    }
  }

  /**
   * Registers any required events. Please don't forget to unsubscibe!
   */
  protected registerEvents(): void {
    //Nothing. Can be overridden
  }

  /**
   * Gets a filter query string
   * @param filter Kedo grid filter
   * @returns The filter string to be sent to the API
   */
  private getFilterQueryString(filter: CompositeFilterDescriptor): string {
    //https://www.telerik.com/blogs/server-side-filtering-kendo-angular-ui-grid
    const hasCustomFilter: boolean = filter?.filters?.length > 0;
    let filterQueryString = '';
    if (hasCustomFilter) {
      const filterCopy = ObjectUtil.deepCopy(filter);
      filterCopy.useV2ServerFilter = true;
      filterQueryString = `&filter=${encodeURIComponent(JSON.stringify(filterCopy))}`;
    }

    return filterQueryString;
  }


  /**
   * A column was reordered
   * @param event
   */
  public columnReordered(event: ColumnReorderEvent): void {
    const column: ColumnBase = event.column;
    setTimeout(() => {//Timeout because Kendo grid is updated with new info in next event loop
      try {
        this.loading = true;//show loader, because user can reorder/resize to fast for API
        this.tableSettingsService.updateGrid(this.area, column, this.kendoGrid.columns.toArray() as ColumnComponent[]).then(() => {
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
      } finally {
        this.loading = false;
      }
    });
  }

  /**
   * A column was resized
   * @param args
   */
  public columnResized(args: ColumnResizeArgs[]): void {
    const column = args[0].column;
    setTimeout(() => {//Timeout because Kendo grid is updated with new info in next event loop
      try {
        this.loading = true;//show loader, because user can reorder/resize to fast for API
        this.tableSettingsService.updateGrid(this.area, column, this.kendoGrid.columns.toArray() as ColumnComponent[]).then(() => {
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
      } finally {
        this.loading = false;
      }
    });
  }

  /**
   * A column was shown or hidden
   * @param event
   */
  public columnVisibilityChanged(event: ColumnVisibilityChangeEvent): void {
    const columns: ColumnBase[] = event.columns;
    const column = columns[0];
    setTimeout(() => {//Timeout because Kendo grid is updated with new info in next event loop
      try {
        this.loading = true;//show loader, because user can reorder/resize to fast for API
        this.tableSettingsService.updateGrid(this.area, column, this.kendoGrid.columns.toArray() as ColumnComponent[]).then(() => {
          this.loading = false;
        }).catch(() => {
          this.loading = false;
        });
      } finally {
        this.loading = false;
      }
    });
  }

  /**
   * The refresh button was clicked
   */
  public refreshGridClicked(): void {
    this.loadData(this.state, null);
  }

  /**
   * Callback to apply CSS classes to grid row. Can be overriden in derived class
   */
  public rowClassCallback = (_context: RowClassArgs) => {
    return {};//default return no css class
  }

  /**
   * trackby helper for *ngFor
   * @param _index Index
   * @param field Field name
   * @returns 
   */
  public trackByField(_index: number, field: any): any {
    return field.idfield;
  }

  /**
   * Recursively gets the Excel export data until done
   * @param resolve Promise: Resolve
   * @param state Grid state
   * @param allData All data loaded so far
   * @param pageSize Page size
   */
  private getExcelExportDataRecursive(resolve, state: State, allData: any[], pageSize: number): void {
    this.loading = true;
    state.take = pageSize;
    const url = this.buildPagedListEndpoint(state);

    const data$: Observable<any> = url ? this.http.get(url) : of([]);
    data$.pipe(take(1)).subscribe((data: any) => {
      allData.push(...data.details);
      state.skip += pageSize;

      if (state.skip > data.total) {
        this.loading = false;
        const result: GridDataResult = {
          data: allData,
          total: allData.length
        }
        resolve(result);
      }
      else {
        setTimeout(() => {
          //After timeout, so we don't spam the server with requests
          this.getExcelExportDataRecursive(resolve, state, allData, 100);
        }, 500);
      }
    });
  }

  /**
   * Enables the asynchronous Excel export of *all* grid data.
   * Needs to be registered at the grid's kendo-grid-excel alike this: [fetchData]="getExcelExportData(2000)"
   * @param limit
   * @returns
   */
  public getExcelExportData(limit: number): () => Promise<GridDataResult> {
    //Must be done like this - otherwise, the this context would be undefined
    return () => {
      return new Promise<GridDataResult>((resolve, reject) => {
        if (!this.useServerSidePaging) {
          alert("This grid doesn't support native server side paging!");
          resolve(null);
          return;
        }
        const stateCopy = JSON.parse(JSON.stringify(this.state)) as State;
        if (this.gridDataResult.total > limit) {
          this.eventBus.broadcastEvent('openConfirmDialog', {
            success: () => {
              this.getExcelExportDataRecursive(resolve, stateCopy, [], 100);
            },
            cancel: () => {
              resolve(null);
            },
            customMessage: () => {
              return 'You are about to export more than ' + limit + ' entries. This might take a while. Are you sure to proceed?'
            },
          }, 'excelExportMoreThanLimit');
        }
        else {
          this.getExcelExportDataRecursive(resolve, stateCopy, [], 100);
        }
      });
    };
  }

  /**
   * Exports the grid to Excel
   * @param grid Grid
   */
  public exportToExcel(grid: GridComponent): void {
    grid.saveAsExcel();
  }

  /**
   * Updates the state
   * @param newState The new State
   */
  private setState(newState: DataStateChangeEvent | State): void {
    this.state.filter = newState.filter;
    this.state.group = newState.group;
    this.state.skip = newState.skip;
    this.state.sort = newState.sort;
    this.state.take = newState.take;
  }

  /**
   * Updates the grid
   * @param data The grid data
   * @param total The total amount of entries
   */
  private processGrid(data: any, total: number): void {
    const skip = this.state.skip;
    if (total > this.loadedData.length) {
      this.state.skip = 0;
    }

    const filter = this.state.filter;
    if (this.useServerSidePaging) {
      this.state.filter = null;
    }

    this.gridDataResult = process(data || [], this.state);
    this.gridDataResult.total = total;

    //reapply skip and filter
    this.state.skip = skip;
    this.state.filter = filter;

    //Also calculate array of items on current page
    const flattenArrays = arr => arr.reduce((a, e) => [...a, ...e], []);
    //grouped grid has "group rows" with items array, so cover both cases
    const arrays = this.gridDataResult.data.map(item => Array.isArray(item.items) ? item.items : [item]);
    this.currentPageItems = flattenArrays(arrays);
  }

  /**
   * Returns flatten array of rows (in case if grouping is enabled gridDataResult.data is a little more complicated)
   */
  public getFlattenGridDataResultData(): any[] {
    const flattenArrays = arr => arr.reduce((a, e) => [...a, ...e], []);
    //grouped grid has "group rows" with items array, so cover both cases
    const arrays = this.gridDataResult.data?.map(item => Array.isArray(item.items) ? item.items : [item]) || [];
    const flattenArray = flattenArrays(arrays);
    return flattenArray;
  }

  /**
   * Checks if a loading operation should be triggered
   * @param gridDataState The Grid's data state
   * @returns true if the grid should load data
   */
  private shouldTriggerLoad(gridDataState: DataStateChangeEvent): boolean {
    const sortChanged = !ArraysUtil.arraysEqual(this.state.sort, gridDataState.sort);
    const groupChanged = !ArraysUtil.arraysEqual(this.state.group, gridDataState.group);
    const skipChanged = this.state.skip !== gridDataState.skip;
    const takeChanged = this.state.take !== gridDataState.take;

    const triggerLoad = skipChanged || takeChanged || (!sortChanged && !groupChanged);
    return triggerLoad;
  }
}
